import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { formatDateWithTime } from "../../../shared/utility";
import LocationZoneStacked from "../../packaging/LocationZoneStacked";
import TextWithLabel from "../../controls/TextWithLabel";

const sectionTitleClass =
  "mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500";

function Field({ label, text, secondaryText, className = "" }) {
  const plu =
    secondaryText != null && String(secondaryText).trim() !== ""
      ? String(secondaryText).trim()
      : null;
  return (
    <div className={`rounded-md bg-gray-50 px-3 py-2 ${className}`}>
      <TextWithLabel label={label} text={text ?? "—"} />
      {plu ? (
        <div className="mt-0.5 text-xs text-gray-500">PLU: {plu}</div>
      ) : null}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="pt-3 first:pt-0">
      <p className={sectionTitleClass}>{title}</p>
      {children}
    </section>
  );
}

function LocationField({ singleItem, className = "" }) {
  return (
    <LocationZoneStacked row={singleItem} variant="field" className={className} />
  );
}

function ItemDetails({ singleItem }) {
  const manufacturedProduct = singleItem?.manufactured_product ?? null;

  return (
    <div className="divide-y">
      <Section title="Product">
        <div className="grid grid-cols-7 gap-x-3 gap-y-2">
          <Field
            className="col-span-2"
            label="Product Name"
            text={singleItem.product_name}
            secondaryText={singleItem.product_plu ?? singleItem.plu}
          />
          <Field label="Brand" text={singleItem.brand_name} />
          <LocationField singleItem={singleItem} />
          <Field
            label="Total Packaged"
            text={singleItem.total_individual_units}
          />
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <div className="mb-1 flex items-start justify-between gap-2">
              <p className="font-12 block font-medium text-gray-600">
                Production Run
              </p>
              {manufacturedProduct?.id ? (
                <a
                  className="shrink-0 rounded-md bg-main-purple px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  href={`/manufactured-products/${manufacturedProduct.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
              ) : null}
            </div>
            <div className="text-base font-medium text-gray-900">
              {manufacturedProduct?.product_name ?? "—"}
            </div>
            {manufacturedProduct?.plu ? (
              <div className="mt-0.5 text-xs text-gray-500">
                PLU: {manufacturedProduct.plu}
              </div>
            ) : null}
            {manufacturedProduct?.id ? (
              <div className="mt-0.5 text-xs text-gray-500">
                ID: {manufacturedProduct.id}
              </div>
            ) : null}
          </div>
          <Field
            label="Production Date"
            text={formatDateWithTime(singleItem.production_date)}
          />
          <Field label="Packing Size" text={singleItem.packing_size} />
          <Field label="Supervisor" text={singleItem.supervisor} />
          {singleItem.employee_1 ? (
            <Field
              className="col-span-2"
              label="Employee(s)"
              text={singleItem.employee_1}
            />
          ) : null}
          <Field
            label="Packaging Date"
            text={formatDateWithTime(singleItem.packaging_date)}
          />
          <Field label="Best Before Date" text={singleItem.best_before_date} />
          <Field label="Shelf Life" text={singleItem.shelf_life} />
          <Field label="Skid Label" text={singleItem.pallet_barcode ?? "N/A"} />
          <Field
            label="Packaging Material 1"
            text={singleItem.packaging_material_1}
          />
          <Field
            label="Packaging Material 2"
            text={singleItem.packaging_material_2}
          />
          <Field
            label="Packaging Material 3"
            text={singleItem.packaging_material_3}
          />
          <Field
            label="Room Temp (°C)"
            text={singleItem.room_temperature}
          />
          <Field
            label="Product Temp (°C)"
            text={singleItem.product_temperature}
          />
        </div>
      </Section>
    </div>
  );
}

export default function PackagingDetails(props) {
  const { packagedItem, setOpen } = props;
  const packagedItems = Array.isArray(packagedItem)
    ? packagedItem
    : packagedItem?.packaged_material ?? [];
  const titleLot = packagedItems?.[0]?.lot_number ?? "Packaging Details";

  const handleClose = () => setOpen({ childComponent: null, open: false });

  return (
    <div className="flex h-full flex-col bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between">
            <div>
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Packaging Details
              </Dialog.Title>
              <p className="mt-1 text-lg text-gray-500">Lot: {titleLot}</p>
            </div>

            <div className="ml-3 flex h-8 items-center">
              <button
                type="button"
                className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={handleClose}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-4 sm:px-6">
          {!packagedItems?.length ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500">
              No packaging details.
            </div>
          ) : (
            <div className="space-y-4">
              {packagedItems.map((singleItem, idx) => (
                <div
                  key={singleItem.id ?? idx}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900">
                        {formatDateWithTime(singleItem.packaging_date) ||
                          "Packaging"}
                      </h2>
                    </div>

                    {packagedItems.length > 1 && (
                      <span className="text-xs text-gray-400">
                        {packagedItems.length - idx} / {packagedItems.length}
                      </span>
                    )}
                  </div>

                  <ItemDetails singleItem={singleItem} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white px-4 py-4">
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-gray-50 hover:ring-gray-400"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
